import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { 
  TrendingUp,
  Users,
  FileText,
  DollarSign,
  CheckCircle2,
  Clock,
  ArrowRight,
  Play,
  Pause,
  CheckCheck,
  AlertCircle,
  Target,
  Phone,
  Calendar,
  Building2,
  Timer
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import NotesWidget from "../components/NotesWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  "January 2026", "February 2026", "March 2026", "April 2026",
  "May 2026", "June 2026", "July 2026", "August 2026",
  "September 2026", "October 2026", "November 2026", "December 2026"
];

// Current month label like "April 2026" — used as default for selectors everywhere
const CURRENT_MONTH = (() => {
  const names = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const d = new Date();
  return `${names[d.getMonth()]} ${d.getFullYear()}`;
})();

const STAGE_COLORS = {
  "New": "bg-blue-100 text-blue-700",
  "Contacted": "bg-purple-100 text-purple-700",
  "Interested": "bg-amber-100 text-amber-700",
  "Negotiations": "bg-orange-100 text-orange-700",
  "Won": "bg-green-100 text-green-700",
  "Lost": "bg-red-100 text-red-700"
};

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [clientSummary, setClientSummary] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [myLeadTasks, setMyLeadTasks] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(CURRENT_MONTH);
  const [smeMonthFilter, setSmeMonthFilter] = useState(CURRENT_MONTH);
  const [smeStatusFilter, setSmeStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every second for live timer
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Calculate elapsed time from task_started_at
  // Elapsed working time: accumulated_seconds (from prior segments) + current segment (if Started)
  const getElapsedTime = useCallback((task) => {
    if (!task) return null;
    try {
      const accumulated = Number(task.accumulated_seconds || 0);
      let current = 0;
      if (task.task_status === "Started" && task.task_started_at) {
        const startTime = new Date(task.task_started_at);
        current = Math.max(0, Math.floor((currentTime - startTime) / 1000));
      }
      const total = accumulated + current;
      const hours = Math.floor(total / 3600);
      const mins = Math.floor((total % 3600) / 60);
      const secs = total % 60;
      if (hours > 0) return `${hours}h ${mins}m ${secs}s`;
      return `${mins}m ${secs}s`;
    } catch {
      return null;
    }
  }, [currentTime]);

  // Format time spent in minutes to readable format
  const formatTimeSpent = (minutes) => {
    if (!minutes) return null;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  useEffect(() => {
    fetchData();
  }, [selectedMonth, user]);

  const fetchData = async () => {
    try {
      const [statsRes, attendanceRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard`),
        axios.get(`${API}/attendance/today`)
      ]);
      setStats(statsRes.data);
      setTodayAttendance(attendanceRes.data);

      const role = user?.role;

      if (role === "admin") {
        const [monthlyRes, onlineRes, tasksRes] = await Promise.all([
          axios.get(`${API}/analytics/monthly/${encodeURIComponent(selectedMonth)}`),
          axios.get(`${API}/attendance/online`),
          axios.get(`${API}/my-tasks`)
        ]);
        setClientSummary(monthlyRes.data);
        setOnlineUsers(onlineRes.data);
        setMyTasks(tasksRes.data);
      } else if (role === "content_manager") {
        try {
          const [onlineRes, tasksRes] = await Promise.all([
            axios.get(`${API}/attendance/online`),
            axios.get(`${API}/my-tasks`)
          ]);
          setOnlineUsers(onlineRes.data);
          setMyTasks(tasksRes.data);
        } catch (e) {
          // Content manager may not have access to online users
          try {
            const tasksRes = await axios.get(`${API}/my-tasks`);
            setMyTasks(tasksRes.data);
          } catch (err) {
            console.error("Error fetching tasks:", err);
          }
        }
      } else if (role === "associate_sme" || role === "sr_sme") {
        const tasksRes = await axios.get(`${API}/my-tasks`);
        setMyTasks(tasksRes.data);
      } else if (role === "bde") {
        try {
          const leadTasksRes = await axios.get(`${API}/my-lead-tasks`);
          setMyLeadTasks(leadTasksRes.data);
        } catch (e) {
          console.error("Error fetching lead tasks:", e);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    try {
      await axios.post(`${API}/attendance/checkin`);
      toast.success("Checked in successfully!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to check in");
    }
  };

  const handleCheckOut = async () => {
    try {
      await axios.post(`${API}/attendance/checkout`);
      toast.success("Checked out successfully!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to check out");
    }
  };

  const handleTaskStatusUpdate = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/my-tasks/${orderId}/status?task_status=${newStatus}`);
      toast.success(`Task marked as ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update task status");
    }
  };

  const handleSectionStatusUpdate = async (orderId, sectionId, newStatus) => {
    try {
      await axios.put(`${API}/my-tasks/${orderId}/sections/${sectionId}/status?task_status=${newStatus}`);
      toast.success(`Section marked as ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to update section status");
    }
  };

  // Flatten multi-section orders: each assigned section becomes its own row.
  // Single-assigned orders (no sub_assignments) remain as one row.
  const flattenMyTasks = useCallback((tasks) => {
    const rows = [];
    (tasks || []).forEach((t) => {
      const mySections = t.my_sections || [];
      if (mySections.length > 0) {
        mySections.forEach((sec) => {
          rows.push({
            ...t,
            id: `${t.id}__${sec.id}`,
            orderId: t.id,
            sectionId: sec.id,
            section_name: sec.section_name,
            task_status: sec.task_status,
            task_started_at: sec.task_started_at,
            task_paused_at: sec.task_paused_at,
            task_finished_at: sec.task_finished_at,
            accumulated_seconds: sec.accumulated_seconds || 0,
            time_spent_minutes: sec.time_spent_minutes,
            isSection: true,
          });
        });
      } else {
        rows.push({ ...t, isSection: false });
      }
    });
    return rows;
  }, []);

  const handleLeadTaskComplete = async (taskId) => {
    try {
      await axios.put(`${API}/lead-tasks/${taskId}?status=Completed`);
      toast.success("Task completed!");
      fetchData();
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const formatRoleName = (role) => {
    const roleMap = {
      admin: "Admin",
      content_manager: "Content Manager",
      associate_sme: "Associate SME",
      sr_sme: "Sr. SME",
      bde: "BDE"
    };
    return roleMap[role] || role;
  };

  const getDashboardTitle = () => {
    const role = user?.role;
    if (role === "admin") return "Admin Dashboard";
    if (role === "content_manager") return "Content Manager Dashboard";
    if (role === "associate_sme" || role === "sr_sme") return "SME Dashboard";
    if (role === "bde") return "Sales Dashboard";
    return "Dashboard";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" data-testid="loading-spinner"></div>
      </div>
    );
  }

  const isAdmin = user?.role === "admin";
  const isContentManager = user?.role === "content_manager";
  const isSME = user?.role === "associate_sme" || user?.role === "sr_sme";
  const isBDE = user?.role === "bde";

  // Calculate totals for admin
  const totalRevenue = clientSummary.reduce((sum, c) => sum + (c.revenue || 0), 0);
  const totalRealised = clientSummary.reduce((sum, c) => sum + (c.realised || 0), 0);
  const totalPending = totalRevenue - totalRealised;
  const totalOrders = clientSummary.reduce((sum, c) => sum + (c.total_orders || 0), 0);
  const completedOrders = clientSummary.reduce((sum, c) => sum + (c.completed_orders || 0), 0);
  const completionRate = totalOrders > 0 ? Math.round((completedOrders / totalOrders) * 100) : 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {getDashboardTitle()}
          </h1>
          <p className="text-slate-500 mt-1">
            Welcome back, {user?.name}
          </p>
        </div>

        {/* Check-in/out */}
        <div className="flex items-center gap-3">
          {todayAttendance?.check_in && !todayAttendance?.check_out && (
            <div className="flex items-center gap-2 text-sm text-green-600">
              <div className="online-indicator"></div>
              Online since {todayAttendance.check_in}
            </div>
          )}
          {!todayAttendance?.check_in ? (
            <button onClick={handleCheckIn} className="btn-primary" data-testid="checkin-btn">
              <Clock size={16} /> Check In
            </button>
          ) : !todayAttendance?.check_out ? (
            <button onClick={handleCheckOut} className="btn-secondary" data-testid="checkout-btn">
              <Clock size={16} /> Check Out
            </button>
          ) : (
            <span className="text-sm text-slate-500 bg-slate-100 px-3 py-1.5 rounded-lg">
              Day completed ({todayAttendance.check_in} - {todayAttendance.check_out})
            </span>
          )}
        </div>
      </div>

      {/* ========== ADMIN DASHBOARD ========== */}
      {isAdmin && (
        <>
          {/* Month Selector */}
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-500">Viewing:</span>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-44 bg-white" data-testid="month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map((month) => (
                  <SelectItem key={month} value={month}>{month}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Financial Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="stats-grid">
            <div className="stat-card">
              <p className="stat-card-title">Total Revenue</p>
              <p className="stat-card-value primary">{formatCurrency(totalRevenue)}</p>
              <p className="stat-card-subtitle">{totalOrders} orders</p>
            </div>
            <div className="stat-card">
              <p className="stat-card-title">Revenue Realised</p>
              <p className="stat-card-value success">{formatCurrency(totalRealised)}</p>
              <p className="stat-card-subtitle">
                {totalRevenue > 0 ? Math.round((totalRealised / totalRevenue) * 100) : 0}% collected
              </p>
            </div>
            <div className="stat-card">
              <p className="stat-card-title">Pending Amount</p>
              <p className="stat-card-value warning">{formatCurrency(totalPending)}</p>
              <p className="stat-card-subtitle">To be collected</p>
            </div>
            <div className="stat-card">
              <p className="stat-card-title">Completion Rate</p>
              <p className="stat-card-value" style={{ color: "#8B5CF6" }}>{completionRate}%</p>
              <div className="progress-bar mt-2">
                <div className="progress-bar-fill" style={{ width: `${completionRate}%` }}></div>
              </div>
              <p className="stat-card-subtitle">{completedOrders}/{totalOrders} completed</p>
            </div>
          </div>

          {/* CRM Stats */}
          {stats?.total_leads > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="crm-stats">
              <div className="stat-card">
                <p className="stat-card-title">Total Leads</p>
                <p className="stat-card-value primary">{stats?.total_leads || 0}</p>
              </div>
              <div className="stat-card">
                <p className="stat-card-title">Pipeline Value</p>
                <p className="stat-card-value">{formatCurrency(stats?.pipeline_value)}</p>
              </div>
              <div className="stat-card">
                <p className="stat-card-title">Won Deals</p>
                <p className="stat-card-value success">{stats?.lead_stage_counts?.Won || 0}</p>
              </div>
              <div className="stat-card">
                <p className="stat-card-title">Won Value</p>
                <p className="stat-card-value success">{formatCurrency(stats?.won_value)}</p>
              </div>
            </div>
          )}

          {/* Client Summary */}
          <div className="stat-card" data-testid="client-summary">
            <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Client Summary - {selectedMonth.split(" ")[0]}
            </h2>
            <div className="space-y-3">
              {clientSummary.map((client) => (
                <Link
                  key={client.client_id}
                  to={`/app/clients/${client.client_id}`}
                  className="flex items-center justify-between p-4 rounded-lg hover:bg-slate-50 transition-colors border border-slate-100"
                  data-testid={`client-summary-${client.client_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-prepify-50 flex items-center justify-center">
                      <span className="text-sm font-semibold text-prepify-600">
                        {client.client_name.charAt(0)}
                      </span>
                    </div>
                    <span className="font-medium text-slate-900">{client.client_name}</span>
                  </div>
                  <div className="flex items-center gap-8 text-sm">
                    <div className="text-right">
                      <p className="text-slate-500">Revenue</p>
                      <p className="font-semibold text-slate-900">{formatCurrency(client.revenue)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-slate-500">Realised</p>
                      <p className="font-semibold text-green-600">{formatCurrency(client.realised)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-slate-500">%RR</p>
                      <p className="font-semibold text-prepify-600">{client.percentage_rr}%</p>
                    </div>
                    <ArrowRight size={16} className="text-slate-400" />
                  </div>
                </Link>
              ))}
              {clientSummary.length === 0 && (
                <p className="text-center text-slate-500 py-8">No client data for this month</p>
              )}
            </div>
          </div>

          {/* My Assigned Tasks (Admin) + Notes/Reminders side-by-side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {myTasks.length > 0 ? (
              <div className="stat-card" data-testid="admin-my-tasks">
                <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  My Assigned Tasks
                </h2>
                <div className="space-y-3">
                  {myTasks.map((task) => {
                    const elapsedTime = (task.task_status === "Started" || task.task_status === "Paused") ? getElapsedTime(task) : null;
                    const totalTime = task.task_status === "Finished" ? formatTimeSpent(task.time_spent_minutes) : null;

                    return (
                      <div
                        key={task.id}
                        className="flex items-center justify-between p-4 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">{task.particulars}</span>
                            <span className={`badge ${
                              task.task_status === "Finished" ? "badge-success" :
                              task.task_status === "Started" ? "badge-info" :
                              task.task_status === "Paused" ? "badge-warning" : "badge-warning"
                            }`}>
                              {task.task_status || "Not Started"}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                            <span>{task.client_name}</span>
                            <span>•</span>
                            <span>{task.exam} - {task.subject}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {(task.task_status === "Started" || task.task_status === "Paused") && elapsedTime && (
                            <div className={`flex items-center gap-1 px-3 py-1.5 rounded-full font-mono text-sm ${task.task_status === "Started" ? "text-blue-600 bg-blue-50" : "text-amber-700 bg-amber-50"}`}>
                              <Timer size={14} className={task.task_status === "Started" ? "animate-pulse" : ""} />
                              {elapsedTime}{task.task_status === "Paused" ? " (paused)" : ""}
                            </div>
                          )}
                          {task.task_status === "Finished" && totalTime && (
                            <div className="flex items-center gap-1 text-green-600 bg-green-50 px-3 py-1.5 rounded-full text-sm">
                              <Clock size={14} />
                              {totalTime}
                            </div>
                          )}
                          {(!task.task_status || task.task_status === "Not Started") && (
                            <button
                              onClick={() => handleTaskStatusUpdate(task.id, "Started")}
                              className="btn-secondary text-sm py-1.5"
                              data-testid={`task-start-${task.id}`}
                            >
                              <Play size={14} /> Start
                            </button>
                          )}
                          {task.task_status === "Started" && (
                            <>
                              <button
                                onClick={() => handleTaskStatusUpdate(task.id, "Paused")}
                                className="btn-secondary text-sm py-1.5"
                                data-testid={`task-pause-${task.id}`}
                              >
                                <Pause size={14} /> Pause
                              </button>
                              <button
                                onClick={() => handleTaskStatusUpdate(task.id, "Finished")}
                                className="btn-primary text-sm py-1.5"
                                data-testid={`task-finish-${task.id}`}
                              >
                                <CheckCheck size={14} /> Finish
                              </button>
                            </>
                          )}
                          {task.task_status === "Paused" && (
                            <>
                              <button
                                onClick={() => handleTaskStatusUpdate(task.id, "Started")}
                                className="btn-secondary text-sm py-1.5"
                                data-testid={`task-resume-${task.id}`}
                              >
                                <Play size={14} /> Resume
                              </button>
                              <button
                                onClick={() => handleTaskStatusUpdate(task.id, "Finished")}
                                className="btn-primary text-sm py-1.5"
                                data-testid={`task-finish-${task.id}`}
                              >
                                <CheckCheck size={14} /> Finish
                              </button>
                            </>
                          )}
                          {task.task_status === "Finished" && (
                            <span className="text-green-600 flex items-center gap-1">
                              <CheckCircle2 size={16} /> Completed
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="stat-card flex flex-col items-center justify-center text-center text-slate-400" data-testid="admin-my-tasks-empty">
                <CheckCircle2 size={28} className="mb-2 opacity-40" />
                <p className="text-sm font-medium text-slate-500">No tasks assigned to you</p>
                <p className="text-xs mt-1">Assigned orders show up here when you're listed as the SME.</p>
              </div>
            )}
            <NotesWidget />
          </div>

          {/* Online Users */}
          {onlineUsers.length > 0 && (
            <div className="stat-card" data-testid="online-users">
              <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Online Now
              </h2>
              <div className="flex flex-wrap gap-3">
                {onlineUsers.map((u) => (
                  <div key={u.id} className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-full">
                    <div className="online-indicator"></div>
                    <span className="text-sm font-medium text-green-700">{u.name}</span>
                    <span className="text-xs text-green-600">({formatRoleName(u.role)})</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ========== CONTENT MANAGER DASHBOARD ========== */}
      {isContentManager && (
        <>
          {/* Content Manager Stats - No Financials */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="cm-stats">
            <div className="stat-card">
              <p className="stat-card-title">Total Clients</p>
              <p className="stat-card-value primary">{stats?.total_clients || 0}</p>
            </div>
            <div className="stat-card">
              <p className="stat-card-title">Total Orders</p>
              <p className="stat-card-value">{stats?.total_orders || 0}</p>
              <p className="stat-card-subtitle">{stats?.assigned_orders || 0} assigned</p>
            </div>
            <div className="stat-card">
              <p className="stat-card-title">Completed</p>
              <p className="stat-card-value success">{stats?.completed_orders || 0}</p>
            </div>
            <div className="stat-card">
              <p className="stat-card-title">Pending</p>
              <p className="stat-card-value warning">{stats?.pending_orders || 0}</p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Link to="/app/all-orders" className="stat-card hover:border-prepify-200 transition-colors group">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-slate-900">All Orders</h3>
                  <p className="text-sm text-slate-500 mt-1">View and manage all orders</p>
                </div>
                <ArrowRight className="text-slate-400 group-hover:text-prepify-500 transition-colors" />
              </div>
            </Link>
            <div className="stat-card">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-slate-900">Team SMEs</h3>
                  <p className="text-sm text-slate-500 mt-1">{stats?.total_smes || 0} SMEs in team</p>
                </div>
                <Users className="text-slate-400" />
              </div>
            </div>
          </div>

          {/* My Assigned Tasks (Content Manager) + Notes/Reminders side-by-side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {myTasks.length > 0 ? (
            <div className="stat-card" data-testid="cm-my-tasks">
              <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                My Assigned Tasks
              </h2>
              <div className="space-y-3">
                {myTasks.map((task) => {
                  const elapsedTime = (task.task_status === "Started" || task.task_status === "Paused") ? getElapsedTime(task) : null;
                  const totalTime = task.task_status === "Finished" ? formatTimeSpent(task.time_spent_minutes) : null;
                  
                  return (
                    <div
                      key={task.id}
                      className="flex items-center justify-between p-4 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900">{task.particulars}</span>
                          <span className={`badge ${
                            task.task_status === "Finished" ? "badge-success" :
                            task.task_status === "Started" ? "badge-info" :
                            task.task_status === "Paused" ? "badge-warning" : "badge-warning"
                          }`}>
                            {task.task_status || "Not Started"}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                          <span>{task.client_name}</span>
                          <span>•</span>
                          <span>{task.exam} - {task.subject}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {(task.task_status === "Started" || task.task_status === "Paused") && elapsedTime && (
                          <div className={`flex items-center gap-1 px-3 py-1.5 rounded-full font-mono text-sm ${task.task_status === "Started" ? "text-blue-600 bg-blue-50" : "text-amber-700 bg-amber-50"}`}>
                            <Timer size={14} className={task.task_status === "Started" ? "animate-pulse" : ""} />
                            {elapsedTime}{task.task_status === "Paused" ? " (paused)" : ""}
                          </div>
                        )}
                        {task.task_status === "Finished" && totalTime && (
                          <div className="flex items-center gap-1 text-green-600 bg-green-50 px-3 py-1.5 rounded-full text-sm">
                            <Clock size={14} />
                            {totalTime}
                          </div>
                        )}
                        {(!task.task_status || task.task_status === "Not Started") && (
                          <button
                            onClick={() => handleTaskStatusUpdate(task.id, "Started")}
                            className="btn-secondary text-sm py-1.5"
                            data-testid={`task-start-${task.id}`}
                          >
                            <Play size={14} /> Start
                          </button>
                        )}
                        {task.task_status === "Started" && (
                          <>
                            <button
                              onClick={() => handleTaskStatusUpdate(task.id, "Paused")}
                              className="btn-secondary text-sm py-1.5"
                              data-testid={`task-pause-${task.id}`}
                            >
                              <Pause size={14} /> Pause
                            </button>
                            <button
                              onClick={() => handleTaskStatusUpdate(task.id, "Finished")}
                              className="btn-primary text-sm py-1.5"
                              data-testid={`task-finish-${task.id}`}
                            >
                              <CheckCheck size={14} /> Finish
                            </button>
                          </>
                        )}
                        {task.task_status === "Paused" && (
                          <>
                            <button
                              onClick={() => handleTaskStatusUpdate(task.id, "Started")}
                              className="btn-secondary text-sm py-1.5"
                              data-testid={`task-resume-${task.id}`}
                            >
                              <Play size={14} /> Resume
                            </button>
                            <button
                              onClick={() => handleTaskStatusUpdate(task.id, "Finished")}
                              className="btn-primary text-sm py-1.5"
                              data-testid={`task-finish-${task.id}`}
                            >
                              <CheckCheck size={14} /> Finish
                            </button>
                          </>
                        )}
                        {task.task_status === "Finished" && (
                          <span className="text-green-600 flex items-center gap-1">
                            <CheckCircle2 size={16} /> Completed
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            ) : (
              <div className="stat-card flex flex-col items-center justify-center text-center text-slate-400" data-testid="cm-my-tasks-empty">
                <CheckCircle2 size={28} className="mb-2 opacity-40" />
                <p className="text-sm font-medium text-slate-500">No tasks assigned to you</p>
                <p className="text-xs mt-1">Assigned orders will appear here when you're listed as the Content Manager.</p>
              </div>
            )}
            <NotesWidget />
          </div>

          {/* Online Users */}
          {onlineUsers.length > 0 && (
            <div className="stat-card" data-testid="online-users">
              <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Team Online
              </h2>
              <div className="flex flex-wrap gap-3">
                {onlineUsers.map((u) => (
                  <div key={u.id} className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-full">
                    <div className="online-indicator"></div>
                    <span className="text-sm font-medium text-green-700">{u.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ========== SME DASHBOARD (Associate & Sr SME) ========== */}
      {isSME && (
        <>
          {/* Task Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="sme-stats">
            <div className="stat-card">
              <p className="stat-card-title">Total Tasks</p>
              <p className="stat-card-value primary">{stats?.total_tasks || 0}</p>
            </div>
            <div className="stat-card">
              <p className="stat-card-title">Not Started</p>
              <p className="stat-card-value warning">{stats?.not_started_tasks || 0}</p>
            </div>
            <div className="stat-card">
              <p className="stat-card-title">In Progress</p>
              <p className="stat-card-value" style={{ color: "#3B82F6" }}>{stats?.started_tasks || 0}</p>
            </div>
            <div className="stat-card">
              <p className="stat-card-title">Finished</p>
              <p className="stat-card-value success">{stats?.finished_tasks || 0}</p>
            </div>
          </div>

          {/* My Tasks (SME) + Notes/Reminders side-by-side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="stat-card" data-testid="my-tasks-list">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                My Assigned Tasks
              </h2>
              <div className="flex items-center gap-2 flex-wrap">
                <Select value={smeMonthFilter} onValueChange={setSmeMonthFilter}>
                  <SelectTrigger className="w-40 h-9 text-sm" data-testid="sme-month-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All months</SelectItem>
                    {MONTHS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
                  {[["all","All"],["Not Started","Not Started"],["Started","Started"],["Finished","Finished"]].map(([val,lbl]) => (
                    <button
                      key={val}
                      onClick={() => setSmeStatusFilter(val)}
                      className={`text-xs px-3 py-1.5 transition ${smeStatusFilter === val ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                      data-testid={`sme-status-filter-${val.replace(" ","-").toLowerCase()}`}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {(() => {
              const flat = flattenMyTasks(myTasks);
              const filtered = flat.filter((t) => {
                if (smeMonthFilter !== "all" && t.month !== smeMonthFilter) return false;
                if (smeStatusFilter !== "all") {
                  const ts = t.task_status || "Not Started";
                  if (ts !== smeStatusFilter) return false;
                }
                return true;
              });
              if (flat.length === 0) return (
                <div className="text-center py-12 text-slate-500">
                  <AlertCircle size={48} className="mx-auto mb-4 text-slate-300" />
                  <p>No tasks assigned to you yet.</p>
                  <p className="text-sm mt-1">Tasks will appear here when assigned by your Content Manager.</p>
                </div>
              );
              if (filtered.length === 0) return (
                <div className="text-center py-10 text-slate-500 text-sm">
                  No tasks match the current filters. Try "All months" or change the status filter.
                </div>
              );
              return (
              <div className="space-y-3">
                <p className="text-xs text-slate-500">Showing {filtered.length} of {flat.length} tasks</p>
                {filtered.map((task) => {
                  const elapsedTime = (task.task_status === "Started" || task.task_status === "Paused") ? getElapsedTime(task) : null;
                  const totalTime = task.task_status === "Finished" ? formatTimeSpent(task.time_spent_minutes) : null;
                  const startFn = task.isSection
                    ? () => handleSectionStatusUpdate(task.orderId, task.sectionId, "Started")
                    : () => handleTaskStatusUpdate(task.id, "Started");
                  const pauseFn = task.isSection
                    ? () => handleSectionStatusUpdate(task.orderId, task.sectionId, "Paused")
                    : () => handleTaskStatusUpdate(task.id, "Paused");
                  const resumeFn = startFn;
                  const finishFn = task.isSection
                    ? () => handleSectionStatusUpdate(task.orderId, task.sectionId, "Finished")
                    : () => handleTaskStatusUpdate(task.id, "Finished");

                  return (
                    <div
                      key={task.id}
                      className="flex items-center justify-between p-4 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors"
                      data-testid={`task-${task.id}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-slate-900">{task.particulars}</span>
                          {task.isSection && (
                            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-prepify-100 text-prepify-700">
                              {task.section_name}
                            </span>
                          )}
                          <span className={`badge ${
                            task.task_status === "Finished" ? "badge-success" :
                            task.task_status === "Started" ? "badge-info" :
                            task.task_status === "Paused" ? "badge-warning" : "badge-warning"
                          }`}>
                            {task.task_status || "Not Started"}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                          <span>{task.client_name}</span>
                          <span>•</span>
                          <span>{task.exam} - {task.subject}</span>
                          <span>•</span>
                          <span>Order: {task.status}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {/* Running/paused timer badge */}
                        {(task.task_status === "Started" || task.task_status === "Paused") && elapsedTime && (
                          <div className={`flex items-center gap-1 px-3 py-1.5 rounded-full font-mono text-sm ${task.task_status === "Started" ? "text-blue-600 bg-blue-50" : "text-amber-700 bg-amber-50"}`}>
                            <Timer size={14} className={task.task_status === "Started" ? "animate-pulse" : ""} />
                            {elapsedTime}{task.task_status === "Paused" ? " (paused)" : ""}
                          </div>
                        )}
                        {/* Total time for finished tasks */}
                        {task.task_status === "Finished" && totalTime && (
                          <div className="flex items-center gap-1 text-green-600 bg-green-50 px-3 py-1.5 rounded-full text-sm">
                            <Clock size={14} />
                            {totalTime}
                          </div>
                        )}

                        {(!task.task_status || task.task_status === "Not Started") && (
                          <button
                            onClick={startFn}
                            className="btn-secondary text-sm py-1.5"
                            data-testid={`start-task-${task.id}`}
                          >
                            <Play size={14} /> Start
                          </button>
                        )}
                        {task.task_status === "Started" && (
                          <>
                            <button
                              onClick={pauseFn}
                              className="btn-secondary text-sm py-1.5"
                              data-testid={`pause-task-${task.id}`}
                            >
                              <Pause size={14} /> Pause
                            </button>
                            <button
                              onClick={finishFn}
                              className="btn-primary text-sm py-1.5"
                              data-testid={`finish-task-${task.id}`}
                            >
                              <CheckCheck size={14} /> Finish
                            </button>
                          </>
                        )}
                        {task.task_status === "Paused" && (
                          <>
                            <button
                              onClick={resumeFn}
                              className="btn-secondary text-sm py-1.5"
                              data-testid={`resume-task-${task.id}`}
                            >
                              <Play size={14} /> Resume
                            </button>
                            <button
                              onClick={finishFn}
                              className="btn-primary text-sm py-1.5"
                              data-testid={`finish-task-${task.id}`}
                            >
                              <CheckCheck size={14} /> Finish
                            </button>
                          </>
                        )}
                        {task.task_status === "Finished" && (
                          <span className="text-green-600 flex items-center gap-1">
                            <CheckCircle2 size={16} /> Completed
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              );
            })()}
          </div>
            <NotesWidget />
          </div>
        </>
      )}

      {/* ========== BDE DASHBOARD ========== */}
      {isBDE && (
        <>
          {/* CRM Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="bde-stats">
            <div className="stat-card">
              <p className="stat-card-title">My Leads</p>
              <p className="stat-card-value primary">{stats?.total_leads || 0}</p>
            </div>
            <div className="stat-card">
              <p className="stat-card-title">Pipeline Value</p>
              <p className="stat-card-value">{formatCurrency(stats?.pipeline_value)}</p>
            </div>
            <div className="stat-card">
              <p className="stat-card-title">Won</p>
              <p className="stat-card-value success">{stats?.won_leads || 0}</p>
            </div>
            <div className="stat-card">
              <p className="stat-card-title">Pending Tasks</p>
              <p className="stat-card-value warning">{stats?.pending_tasks || 0}</p>
            </div>
          </div>

          {/* Lead Stage Distribution */}
          {stats?.stage_counts && (
            <div className="stat-card">
              <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Lead Pipeline
              </h3>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                {Object.entries(stats.stage_counts).map(([stage, count]) => (
                  <div key={stage} className={`p-3 rounded-lg ${STAGE_COLORS[stage]}`}>
                    <p className="text-xs font-medium opacity-70">{stage}</p>
                    <p className="text-xl font-bold">{count}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Link to="/app/sales-crm" className="stat-card hover:border-prepify-200 transition-colors group">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-slate-900">Sales CRM</h3>
                  <p className="text-sm text-slate-500 mt-1">Manage leads and track pipeline</p>
                </div>
                <Target className="text-prepify-500" />
              </div>
            </Link>
            <Link to="/app/attendance" className="stat-card hover:border-prepify-200 transition-colors group">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-slate-900">Attendance</h3>
                  <p className="text-sm text-slate-500 mt-1">Track your attendance and leave</p>
                </div>
                <Calendar className="text-slate-400 group-hover:text-prepify-500 transition-colors" />
              </div>
            </Link>
          </div>

          {/* Pending Lead Tasks */}
          {myLeadTasks.length > 0 && (
            <div className="stat-card" data-testid="lead-tasks-list">
              <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Pending Tasks
              </h2>
              <div className="space-y-3">
                {myLeadTasks.map((task) => (
                  <div
                    key={task.id}
                    className="flex items-center justify-between p-4 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors"
                    data-testid={`lead-task-${task.id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Phone size={14} className="text-prepify-500" />
                        <span className="font-medium text-slate-900">{task.title}</span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Building2 size={12} />
                          {task.lead_name}
                        </span>
                        {task.due_date && (
                          <>
                            <span>•</span>
                            <span className="flex items-center gap-1">
                              <Calendar size={12} />
                              Due: {new Date(task.due_date).toLocaleDateString()}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleLeadTaskComplete(task.id)}
                      className="btn-primary text-sm py-1.5"
                      data-testid={`complete-lead-task-${task.id}`}
                    >
                      <CheckCheck size={14} /> Complete
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {myLeadTasks.length === 0 && (
            <div className="stat-card text-center py-12 text-slate-500">
              <Target size={48} className="mx-auto mb-4 text-slate-300" />
              <p>No pending tasks.</p>
              <p className="text-sm mt-1">
                <Link to="/app/sales-crm" className="text-prepify-600 hover:underline">
                  Go to Sales CRM
                </Link>{" "}
                to manage your leads.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Dashboard;
